import { Compass, Scale, ShieldCheck, UserCheck } from "lucide-react";
import { Reveal, SectionHeading } from "./anim";

const DIFFERENTIATORS = [
  { icon: Scale, title: "Independent & Objective", body: "No products to sell you. Our only agenda is the quality of your next decision." },
  { icon: Compass, title: "Real-World Experience", body: "Guidance grounded in actual business and wealth-building, not theory." },
  { icon: UserCheck, title: "Personal by Design", body: "Someone who knows your goals and is as invested in your outcome as you are." },
  { icon: ShieldCheck, title: "Honest About Fit", body: "If we are not the right partner for you, we will tell you in the first conversation." },
];

const PERSONAS = [
  {
    title: "Entrepreneurs in Growth Mode",
    body: "You are building something real, and the decisions are getting bigger. We help you pressure-test each move before you commit to it.",
    quote: "I am ready to take the next step. I just need the confidence and strategy to do it.",
  },
  {
    title: "Business Owners at a Crossroads",
    body: "Expansion, acquisition, a new venture. When the numbers matter, an objective outside perspective changes the outcome.",
    quote: "I need someone to help me turn what I have into something bigger.",
  },
  {
    title: "Investors Evaluating Opportunities",
    body: "You have capital, or an opportunity in front of you. We help you see the risks, the numbers, and the potential outcomes clearly before you act.",
    quote: "I have an opportunity. I just need someone experienced to help me make the right move.",
  },
];

export default function WhoWeHelp() {
  return (
    <section id="who-we-help" data-testid="who-we-help-section" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
      <SectionHeading
        overline="Who We Help"
        title="Built for people who are ready to move, and want to move well."
        copy="Our clients have the ambition and the resources. What they want is the experience, strategy, and outside perspective to take the next step with confidence."
      />

      <div className="mt-16 grid gap-5 md:grid-cols-3">
        {PERSONAS.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.12}>
            <article
              data-testid={`who-card-${i + 1}`}
              className="group relative flex h-full flex-col border border-white/10 bg-[#0F0F12] p-8 transition-[border-color,transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-[#C5A059]/40 hover:shadow-[0_24px_70px_rgba(0,0,0,0.5)]"
            >
              <span
                className="absolute inset-x-0 top-0 h-px origin-left scale-x-0 bg-gradient-to-r from-[#C5A059] to-transparent transition-transform duration-700 group-hover:scale-x-100"
                aria-hidden="true"
              />
              <span className="text-outline-gold text-5xl font-semibold" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-6 text-lg font-medium tracking-tight text-[#F9F8F6] sm:text-xl">{p.title}</h3>
              <p className="mt-4 flex-1 text-sm font-light leading-relaxed text-[#A1A1AA]">{p.body}</p>
              <blockquote className="mt-8 border-l-2 border-[#C5A059]/50 pl-4 text-sm font-light italic leading-relaxed text-[#C9C9CF]">
                {p.quote}
              </blockquote>
            </article>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15}>
        <div data-testid="not-for-panel" className="mt-16 border border-[#C5A059]/25 bg-gradient-to-r from-[#C5A059]/[0.06] to-transparent p-8 sm:p-10">
            <h3 className="text-lg font-medium tracking-tight text-[#C5A059]">Who this is not for</h3>
            <p className="mt-3 max-w-3xl text-sm font-light leading-relaxed text-[#C9C9CF]">
              If you are looking for quick, guaranteed returns, if you are unwilling to take calculated risks, or
              if you want someone else to make every decision for you, we are not the right fit. Wealth is built
              through informed decisions, and that requires your participation.
            </p>
          </div>
      </Reveal>

      <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {DIFFERENTIATORS.map((d, i) => (
          <Reveal key={d.title} delay={i * 0.08} className="h-full">
            <article
              data-testid={`why-card-${i + 1}`}
              className="group flex h-full items-start gap-4 border border-white/10 p-5 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-[#C5A059]/40"
            >
              <d.icon size={19} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#C5A059]" aria-hidden="true" />
              <div>
                <h3 className="text-sm font-semibold tracking-tight text-[#F9F8F6]">{d.title}</h3>
                <p className="mt-1.5 text-xs font-light leading-relaxed text-[#A1A1AA]">{d.body}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
